<template>
  <div class="flex flex-col gap-6">
    <SettingsCard
      v-if="flags.enabledWebPushNotifications"
      title="Notificações push"
      description="Receba alertas no navegador mesmo com o Minha Escala fechado."
    >
      <div class="flex items-center justify-between gap-4">
        <div class="min-w-0">
          <p class="text-base font-medium text-gray-900">Ativar notificações neste dispositivo</p>
          <p class="text-sm text-gray-500 mt-0.5">{{ pushStatusText }}</p>
        </div>
        <Switch
          :model-value="pushEnabled"
          :loading="isRegistering"
          :disabled="pushBlocked"
          data-testid="notifications-push-toggle"
          @update:model-value="togglePush"
        />
      </div>

      <p v-if="pushBlocked" class="mt-3 text-sm text-warning">
        As notificações estão bloqueadas nas configurações do navegador. Libere-as para este site
        para poder ativá-las.
      </p>
    </SettingsCard>

    <ComingSoonCard
      v-else
      :icon="mdiBellOutline"
      title="Notificações push"
      description="Em breve você poderá ativar alertas no navegador para não perder atualizações."
      test-id="notifications-push-coming-soon"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useToast } from 'vue-toastification'
import { mdiBellOutline } from '@mdi/js'

import Switch from '@/components/designSystem/Switch/index.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePushNotifications } from '@/hooks/usePushNotifications'

import SettingsCard from '../atoms/SettingsCard.vue'
import ComingSoonCard from '../atoms/ComingSoonCard.vue'

const FCM_TOKEN_KEY = '@ME:fcm_token'

const { flags } = useFeatureFlags()
const toast = useToast()
const { permissionStatus, isRegistering, requestPermission, registerAndSubscribe, unsubscribe } =
  usePushNotifications()

const pushEnabled = ref(false)

const pushBlocked = computed(() => permissionStatus.value === 'denied')

const pushStatusText = computed(() => {
  if (pushBlocked.value) return 'Bloqueado pelo navegador'
  return pushEnabled.value ? 'Ativado neste dispositivo' : 'Desativado'
})

onMounted(() => {
  if (!flags.enabledWebPushNotifications) return
  permissionStatus.value = 'Notification' in window ? Notification.permission : 'denied'
  pushEnabled.value = permissionStatus.value === 'granted' && !!localStorage.getItem(FCM_TOKEN_KEY)
})

const togglePush = async (value: boolean) => {
  if (value) {
    const granted = await requestPermission()
    if (!granted) {
      pushEnabled.value = false
      toast.error('Não foi possível ativar as notificações. Verifique as permissões do navegador.')
      return
    }
    await registerAndSubscribe()
    pushEnabled.value = true
    toast.success('Notificações push ativadas!')
  } else {
    await unsubscribe()
    pushEnabled.value = false
    toast.success('Notificações push desativadas.')
  }
}
</script>
